import { COUNTRY_MAP } from "Services/constants";
import { DynamicCultureNameSetting, DynamicSettings } from "./DynamicSettings";
import { useSaveDynamicSettings } from "./useSaveDynamicSettings";
import classNames from "classnames";
import { html } from "lit-html";

export interface Props {
	newSettings: DynamicSettings;
	setNewSettings: (settings: DynamicSettings) => void;
}

interface HandleInputChangeParams {
	e: InputEvent;
	setting: DynamicCultureNameSetting;
	key: keyof DynamicCultureNameSetting;
}

export const useCultureNameSettings = (props: Props) => {
	const saver = useSaveDynamicSettings({ newSettings: props.newSettings });

	const formatCulture = (name: string) => {
		const [language, country] = name.split("-");
		return `${language.toLowerCase()}-${country.toUpperCase()}`;
	};

	const handleInputChange = (params: HandleInputChangeParams) => {
		const { e, setting, key } = params;
		const value = (e.target as HTMLInputElement).value;

		const oldSetting = props.newSettings.DynamicCultureNameSettings.find(
			(newSetting) => setting.Culture.toLowerCase() === newSetting.Culture.toLowerCase()
		);
		const newCultureNameSettings = oldSetting
			? props.newSettings.DynamicCultureNameSettings.map((newSetting) =>
					setting.Culture.toLowerCase() === newSetting.Culture.toLowerCase()
						? { ...newSetting, [key]: value }
						: newSetting
				)
			: [...props.newSettings.DynamicCultureNameSettings, { ...setting, [key]: value }];

		props.setNewSettings({ ...props.newSettings, DynamicCultureNameSettings: newCultureNameSettings });
	};

	const rowTemplate = (culture: string, index: number) => {
		const setting: DynamicCultureNameSetting = props.newSettings.DynamicCultureNameSettings.find(
			(s) => s.Culture.toLowerCase() === culture.toLowerCase()
		) || { Culture: culture, Es: "", En: "", Pt: "" };
		return html`
			<tr class=${classNames("pb-4 border-b border-solid border-n-blue", { "bg-gray-100": index % 2 === 0 })}>
				<td class="w-16 px-2 py-4 align-middle">
					<label class="mt-5 block text-right font-bold">${formatCulture(setting.Culture)}</label>
				</td>
				<td class="px-1 py-4">
					<label class="mb-2 block text-sm font-bold">ES</label>
					<input
						class="flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4"
						value=${setting.Es}
						@input=${(e: InputEvent) => handleInputChange({ e, setting, key: "Es" })}
					/>
				</td>
				<td class="px-1 py-4">
					<label class="mb-2 block text-sm font-bold">EN</label>
					<input
						class="flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4"
						value=${setting.En}
						@input=${(e: InputEvent) => handleInputChange({ e, setting, key: "En" })}
					/>
				</td>
				<td class="px-1 py-4">
					<label class="mb-2 block text-sm font-bold">PT</label>
					<input
						class="flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4"
						value=${setting.Pt}
						@input=${(e: InputEvent) => handleInputChange({ e, setting, key: "Pt" })}
					/>
				</td>
			</tr>
		`;
	};

	const htmlTemplate = () => html`
		<section class="mt-5 max-w-[640px]">
			<table class="mb-5 w-full">
				${Array.from(COUNTRY_MAP.keys()).map(rowTemplate)}
			</table>
			${saver.htmlTemplate()}
		</section>
	`;

	return { htmlTemplate };
};
